/**
 * The deal model (design aiCat, L29082): from a facility's underwriting inputs, the what-if
 * DCF, entry and exit, returns and the scenario composite. Every figure it produces is
 * MODELED and the page labels it so; the inputs come from the server (prospect `inputs`).
 * The constants are the design's own assumptions, kept here in one place.
 */
import type { DealInputs, FinancialProspect, MacroRates } from '@aithora/shared';

/** The design's discount rate, used only when the server has no live WACC (named as a fallback wherever it shows). */
export const WACC_FALLBACK = 0.112;

/**
 * The rate the DCF discounts at: the server's live WACC (src/financial/macro.ts, FRED
 * 10-year Treasury and Moody's BAA) when it has one, else WACC_FALLBACK, and the words
 * that say which.
 */
export interface DiscountRate {
  wacc: number;
  live: boolean;
  note: string;
}
export const FALLBACK_RATE: DiscountRate = { wacc: WACC_FALLBACK, live: false, note: 'fallback, no live rates' };
export function discountRateOf(m: MacroRates | null | undefined): DiscountRate {
  const w = m?.suggested?.wacc;
  if (!m?.live || w == null || !Number.isFinite(w) || w <= G) return FALLBACK_RATE;
  return { wacc: w, live: true, note: 'live, FRED ' + String(m.asOf).slice(0, 10) };
}
/** "WACC 9.8% (live, FRED 2026-10-14)". */
export const waccText = (d: { wacc: number; waccNote: string }) => 'WACC ' + (d.wacc * 100).toFixed(1) + '% (' + d.waccNote + ')';
export const G = 0.025;
export const LEV = 0.6;
export const GROW = [0.084, 0.065, 0.053, 0.04, 0.031];
/** Revenue per patient day by payer, $ (Medicare, Medicaid, private). */
export const PPD = { medicare: 638, medicaid: 246, private: 338 } as const;

export type Grade = 'A' | 'B' | 'C' | 'D';
/** Acquired and Pipeline come from the deal book; Map is a building carried over from the
 *  Relationship Map or Find ("Run in Scenario"), underwritten on assumptions. */
export type DealStatus = 'Acquired' | 'Pipeline' | 'Map';

export interface DcfYear {
  year: string;
  ebitda: string;
  fcf: string;
  pv: string;
  e5: number;
}

export interface Deal {
  id: string;
  providerId: string | null;
  prospectStatus: FinancialProspect['status'];
  name: string;
  city: string;
  stt: string;
  type: string;
  beds: number;
  occ: number;
  stars: number;
  ebitda: number;
  mult: number;
  status: DealStatus;
  hprd: number;
  qual: number;
  priv: number;
  agency: number;
  mcare: number;
  mcaid: number;
  ppd: number;
  pd: number;
  rev: number;
  margin: number;
  comp: number;
  grade: Grade;
  rows: DcfYear[];
  tvpv: number;
  ev: number;
  entry: number;
  debt: number;
  eqIn: number;
  e5: number;
  exitEV: number;
  exitEq: number;
  /** Returns on the Deal model's "Buy both" base case (V4 one deal card): MOIC and IRR. */
  moic: number;
  irr: number;
  /** Annual debt service on the bank loan (60% LTV, 7.25%, 25-yr amortization), $M. */
  ds: number;
  /** The same return method for any start EBITDA and exit multiple, at this price and debt. */
  ret: (e0: number, exitMult: number) => { irr: number; moic: number };
  /** Payer-blended yearly EBITDA growth the return method compounds. */
  gB: number;
  peerN: number | null;
  /** The discount rate this deal's DCF ran at, and whether it is live or the fallback. */
  wacc: number;
  waccNote: string;
  metrics: Record<string, number | null>;
  /** Where the payer mix came from, from the server: 'COST REPORT FY2024', 'MODELED' or 'ESTIMATED'. */
  payerMixLabel: string;
}

/** $M the design's way: $3.10M under $10M, $28.3M above, $515K under $1M. */
export const M = (x: number) => (Math.abs(x) >= 1 ? '$' + x.toFixed(x < 10 ? 2 : 1) + 'M' : '$' + Math.round(x * 1000) + 'K');
/** $K the design's way ($94.8K, $512). */
export const K = (x: number) => (Math.abs(x) >= 1000 ? '$' + (x / 1000).toFixed(1) + 'K' : '$' + Math.round(x));
/** A percentage (0 decimals unless asked). */
export const pc = (x: number, d?: number) => (x * 100).toFixed(d === undefined ? 0 : d) + '%';
/** The minus sign the design uses. */
export const MINUS = '−';
export const signed = (x: number, f: (v: number) => string = M) => (x >= 0 ? '+' : MINUS) + f(Math.abs(x));

export const GC: Record<Grade, string> = { A: '#1F6B52', B: '#2F5C97', C: '#5B6B7C', D: '#B3261E' };

export const gradeOf = (comp: number): Grade => (comp >= 80 ? 'A' : comp >= 70 ? 'B' : comp >= 60 ? 'C' : 'D');

export function composite(occ: number, priv: number, stars: number, hprd: number, qual: number): number {
  return (
    25 * Math.min(1, Math.max(0, (occ - 0.55) / 0.43)) +
    20 * Math.min(1, priv / 0.45) +
    20 * (stars / 5) +
    15 * Math.min(1, hprd / 4) +
    20 * (qual / 100)
  );
}

/** The bank loan the return method assumes (V4 aiCat): 7.25%, 25-yr amortization. */
export const LOAN_RATE = 0.0725;
export const LOAN_YEARS = 25;

/** Yearly EBITDA growth blended by payer: Medicare 3.2%, Medicaid 2.5%, private 3.5%, less 0.5 pt. */
export const payerGrowth = (mcare: number, mcaid: number, priv: number) => (mcare * 3.2 + mcaid * 2.5 + priv * 3.5 - 0.5) / 100;

/** Level annual payment on `debt` at LOAN_RATE over LOAN_YEARS. */
export const debtService = (debt: number) => (debt * LOAN_RATE) / (1 - Math.pow(1 + LOAN_RATE, -LOAN_YEARS));

/**
 * The one return method every tab reads (V4 "one deal card per facility"): the Deal model's
 * "Buy both" base case. Equity in at year 0; each year EBITDA grows at the payer-blended rate
 * and the cash after debt service counts; year 5 adds the exit at `exitMult` less the loan
 * balance. IRR by bisection on the NPV; MOIC = cash back / equity in. MODELED.
 */
export function buyBothReturn(e0: number, exitMult: number, eqIn: number, debt: number, gB: number): { irr: number; moic: number } {
  const ds = debtService(debt);
  const balAt = (y: number) => debt * Math.pow(1 + LOAN_RATE, y) - (ds * (Math.pow(1 + LOAN_RATE, y) - 1)) / LOAN_RATE;
  const c = [-eqIn];
  let x = e0;
  let got = 0;
  for (let y = 1; y <= 5; y++) {
    x *= 1 + gB;
    let v = x - ds;
    if (y === 5) v += x * exitMult - balAt(5);
    c.push(v);
    got += v;
  }
  const npv = (r: number) => c.reduce((a, q, i) => a + q / Math.pow(1 + r, i), 0);
  let lo = -0.9;
  let hi = 3;
  if (npv(lo) * npv(hi) > 0) return { irr: got > 0 ? 3 : -0.9, moic: got / eqIn };
  for (let k = 0; k < 80; k++) {
    const mid = (lo + hi) / 2;
    if (npv(lo) * npv(mid) <= 0) hi = mid;
    else lo = mid;
  }
  return { irr: (lo + hi) / 2, moic: got / eqIn };
}

/** The verdict on the deal card: PURSUE at a 12%+ IRR, PURSUE ON TERMS at 8-12%, else PASS. */
export type Verdict = 'PURSUE' | 'PURSUE ON TERMS' | 'PASS';
export const verdictOf = (irr: number): Verdict => (irr >= 0.12 ? 'PURSUE' : irr >= 0.08 ? 'PURSUE ON TERMS' : 'PASS');

/**
 * A building carried over from the Relationship Map or Find ("Run in Scenario"): only its
 * beds, occupancy and payer revenue are known, so everything else is a neutral assumption
 * the page badges MODELED EST. (V4 aiCat: EBITDA 10% of payer revenue, entry 5.0x, 3 stars,
 * 3.48 HPRD, quality 70, agency 8%).
 */
export interface MapSubject {
  name: string;
  beds: number;
  /** Occupancy in percent (the map's unit). */
  occ: number;
  role: string;
  payers: { n: string; u: number }[];
  /** The server's label on those payer figures: 'COST REPORT FY2024', 'MODELED' or 'ESTIMATED'. */
  payersLabel?: string;
  /** The catalog id when the building came from the catalog. */
  id?: string;
}

export function mapSubjectDeal(ms: MapSubject | null | undefined, rate: DiscountRate = FALLBACK_RATE): Deal | null {
  if (!ms) return null;
  const rev = (ms.payers || []).reduce((s, p) => s + (p.u || 0), 0);
  if (!ms.beds || !ms.occ || !(rev > 0)) return null;
  // The server's own split (payer-mix.ts): "Medicare" alone is Medicare; "Other (private,
  // insurance, Medicare Advantage)" and "Private pay" are the private share. No fallback split.
  const sh = (re: RegExp) => (ms.payers || []).filter((p) => re.test(p.n)).reduce((s, p) => s + p.u, 0) / rev;
  const pv = sh(/^(private|other)/i);
  const mc = sh(/^medicare$/i);
  const label = ms.payersLabel ?? 'MODELED';
  const stt = String(ms.role || '').split('·').pop()!.trim();
  return dealOf({
    id: 'map:' + (ms.id ?? ms.name),
    provider_id: ms.id ?? null,
    name: ms.name + ' (from map)',
    state: stt,
    status: 'tracked',
    mapSubject: true,
    inputs: {
      city: '', beds: ms.beds, occupancy: ms.occ / 100, stars: 3, ebitdaM: rev * 0.1, entryMultiple: 5.0, hprd: 3.48, quality: 70,
      privateMix: pv, agencyShare: 0.08, medicareMix: mc, type: 'SNF', peerN: null, metrics: {}, payerMixLabel: label,
      modeled: ['ebitdaM', 'entryMultiple', 'stars', 'hprd', 'quality', 'agencyShare', ...(label.startsWith('COST REPORT') ? [] : ['privateMix', 'medicareMix'])],
    },
  } as unknown as FinancialProspect & { inputs: DealInputs }, rate);
}

/** One facility through the model. */
export function dealOf(p: FinancialProspect & { inputs: DealInputs }, rate: DiscountRate = FALLBACK_RATE): Deal {
  const i = p.inputs;
  const WACC = rate.wacc;
  const beds = i.beds;
  const occ = i.occupancy;
  const stars = i.stars;
  const ebitda = i.ebitdaM;
  const mult = i.entryMultiple;
  const hprd = i.hprd;
  const qual = i.quality;
  const priv = i.privateMix;
  const agency = i.agencyShare;
  const mcare = i.medicareMix;
  const mcaid = 1 - priv - mcare;
  const ppd = mcare * PPD.medicare + mcaid * PPD.medicaid + priv * PPD.private;
  const pd = beds * occ * 365;
  const rev = (pd * ppd) / 1e6;
  const margin = ebitda / rev;
  const comp = composite(occ, priv, stars, hprd, qual);
  const grade = gradeOf(comp);
  let e = ebitda;
  let pvsum = 0;
  let fcf5 = 0;
  const rows: DcfYear[] = [];
  for (let k = 0; k < 5; k++) {
    e = e * (1 + GROW[k]);
    const fcf = e * 0.681;
    const pv = fcf / Math.pow(1 + WACC, k + 1);
    pvsum += pv;
    fcf5 = fcf;
    rows.push({ year: String(2027 + k), ebitda: M(e), fcf: M(fcf), pv: M(pv), e5: e });
  }
  const tvpv = (fcf5 * (1 + G)) / (WACC - G) / Math.pow(1 + WACC, 5);
  const ev = pvsum + tvpv;
  const entry = ebitda * mult;
  const debt = entry * LEV;
  const eqIn = entry - debt;
  const e5 = rows[4].e5;
  const exitEV = e5 * mult;
  const exitEq = exitEV - debt;
  const gB = payerGrowth(mcare, mcaid, priv);
  const ds = debtService(debt);
  const ret = (e0: number, xm: number) => buyBothReturn(e0, xm, eqIn, debt, gB);
  const { irr, moic } = ret(ebitda, mult);
  return {
    id: p.id,
    providerId: p.provider_id,
    prospectStatus: p.status,
    name: p.name,
    city: i.city,
    stt: p.state ?? '',
    type: i.type || 'SNF',
    beds,
    occ,
    stars,
    ebitda,
    mult,
    status: (p as { mapSubject?: boolean }).mapSubject ? 'Map' : p.status === 'acquired' ? 'Acquired' : 'Pipeline',
    hprd,
    qual,
    priv,
    agency,
    mcare,
    mcaid,
    ppd,
    pd,
    rev,
    margin,
    comp,
    grade,
    rows,
    tvpv,
    ev,
    entry,
    debt,
    eqIn,
    e5,
    exitEV,
    exitEq,
    moic,
    irr,
    ds,
    ret,
    gB,
    peerN: i.peerN ?? null,
    metrics: i.metrics ?? {},
    payerMixLabel: i.payerMixLabel ?? 'MODELED',
    wacc: rate.wacc,
    waccNote: rate.note,
  };
}

/** The prospects the model can run on (those the server sent inputs for), in its order. */
export function dealsOf(prospects: readonly FinancialProspect[], rate: DiscountRate = FALLBACK_RATE): Deal[] {
  return prospects.filter((p): p is FinancialProspect & { inputs: DealInputs } => !!p.inputs).map((p) => dealOf(p, rate));
}

/** The design's bed band for a peer group. */
export const bandOfBeds = (beds: number) => (beds < 100 ? '60–99' : beds < 150 ? '100–149' : '150+');
